import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import { FormsModule } from '@angular/forms';

import { Cliente } from '@core/models/models';
import { ClienteService } from '@core/services/domain-services';
import { FeedbackService } from '@core/services/feedback.service';
import {
  EnumDocumentStatus,
  FeDocumento,
  FeReciboItem,
  FeSaldoDocumento,
  FormatoDocumentoPdf,
} from '@shared/api/models/faturacao-eletronica.model';
import { FaturacaoEletronicaService } from '@shared/api/services/faturacao-eletronica.service';

@Component({
  selector: 'app-fatura-detalhe',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ButtonModule,
    DialogModule,
    InputNumberModule,
    SkeletonModule,
    TooltipModule,
    FormsModule,
  ],
  templateUrl: './fatura-detalhe.component.html',
  styleUrl: './fatura-detalhe.component.scss',
})
export class FaturaDetalheComponent implements OnInit {
  private readonly faturacaoService = inject(FaturacaoEletronicaService);
  private readonly clienteService = inject(ClienteService);
  private readonly feedback = inject(FeedbackService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  documentoId!: number;
  documento = signal<FeDocumento | null>(null);
  cliente = signal<Cliente | null>(null);
  saldo = signal<FeSaldoDocumento | null>(null);
  recibos = signal<FeReciboItem[]>([]);

  loading = signal<boolean>(true);
  loadingCliente = signal<boolean>(false);
  loadingSaldo = signal<boolean>(false);
  actionLoading = signal<boolean>(false);
  consultandoEstado = signal<boolean>(false);
  downloadingPdf = signal<boolean>(false);

  // Aba ativa: 'fatura', 'pagamento', 'auditoria'
  activeTab = signal<'fatura' | 'pagamento' | 'auditoria'>('fatura');

  // Modal para Simular Pagamento
  modalSimularVisible = false;
  simularValor: number = 0;
  simulandoPagamento = false;

  // Modal de confirmação para ações irreversíveis
  modalConfirmarAcao = false;
  acaoPendente: {
    titulo: string;
    mensagem: string;
    executar: () => void;
  } | null = null;

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.documentoId = Number(idParam);
      this.carregarDadosCompletos();
    } else {
      this.feedback.showError('Identificador de fatura inválido.');
      this.router.navigate(['/admin/financeiro']);
    }
  }

  carregarDadosCompletos(): void {
    this.loading.set(true);

    this.faturacaoService.obterDocumento(this.documentoId).subscribe({
      next: (doc) => {
        this.documento.set(doc);
        this.loading.set(false);

        // Se o documento possui idCliente, busca dados completos do cliente
        if (doc && doc.idCliente) {
          this.carregarCliente(doc.idCliente);
        }

        // Se a fatura estiver Aceite ou Paga, consultar saldo e recibos
        if (this.isAceite(doc) || this.isPaga(doc)) {
          this.carregarSaldoERecibos();
        }
      },
      error: (err) => {
        this.loading.set(false);
        this.feedback.showError(
          err,
          'Não foi possível carregar os dados da fatura.',
        );
      },
    });
  }

  private carregarCliente(idCliente: number): void {
    this.loadingCliente.set(true);
    this.clienteService.getById(idCliente).subscribe({
      next: (cli) => {
        this.cliente.set(cli);
        this.loadingCliente.set(false);
        const doc = this.documento();
        if (doc && !doc.cliente) {
          doc.cliente = {
            id: cli.id,
            nome: cli.nome,
            nif: cli.nif,
            email: cli.email,
            endereco: cli.endereco,
            numTelefone: cli.numTelefone,
            nomeResponsavel: cli.nomeResponsavel,
            sector: cli.sector,
          };
        }
      },
      error: (err) => {
        this.loadingCliente.set(false);
        console.warn('Erro ao carregar detalhes do cliente:', err);
      },
    });
  }

  private carregarSaldoERecibos(): void {
    this.loadingSaldo.set(true);

    this.faturacaoService.obterSaldoDocumento(this.documentoId).subscribe({
      next: (s) => {
        this.saldo.set(s);
        this.loadingSaldo.set(false);
        if (s && s.saldoEmDivida) {
          this.simularValor = s.saldoEmDivida;
        }
      },
      error: () => {
        this.loadingSaldo.set(false);
      },
    });

    this.faturacaoService.listarRecibos(this.documentoId).subscribe({
      next: (rcs) => {
        this.recibos.set(rcs || []);
      },
      error: () => {
        this.recibos.set([]);
      },
    });
  }

  // Verificadores de Estado
  isGuardada(doc = this.documento()): boolean {
    return (
      doc?.estado === EnumDocumentStatus.Guardada || doc?.estado === 'Guardada'
    );
  }

  isSubmetida(doc = this.documento()): boolean {
    return (
      doc?.estado === EnumDocumentStatus.Submetida ||
      doc?.estado === 'Submetida'
    );
  }

  isAceite(doc = this.documento()): boolean {
    return (
      doc?.estado === EnumDocumentStatus.Aceite || doc?.estado === 'Aceite'
    );
  }

  isRejeitada(doc = this.documento()): boolean {
    return (
      doc?.estado === EnumDocumentStatus.Rejeitada ||
      doc?.estado === 'Rejeitada'
    );
  }

  isPaga(doc = this.documento()): boolean {
    return (
      doc?.paymentStatus === 'PagoTotalmente' ||
      (this.saldo()?.saldoEmDivida === 0 &&
        (this.saldo()?.valorPago ?? 0) > 0) ||
      this.recibos().length > 0
    );
  }

  // Ações de Faturação
  submeterAgt(): void {
    this.abrirConfirmacao(
      'Submeter Fatura à AGT',
      `Confirma a submissão fiscal da fatura ${this.documento()?.documentNo} para a AGT? Esta ação sela o documento e impede novas edições diretas.`,
      () => {
        this.actionLoading.set(true);
        this.faturacaoService.submeterDocumento(this.documentoId).subscribe({
          next: (docAtualizado) => {
            this.actionLoading.set(false);
            this.feedback.showSuccess(
              docAtualizado,
              `Fatura ${docAtualizado.documentNo || ''} submetida à AGT com sucesso (Estado: Submetida).`,
            );
            this.documento.set(docAtualizado);
          },
          error: (err) => {
            this.actionLoading.set(false);
            this.feedback.showError(err, 'Erro ao submeter a fatura à AGT.');
          },
        });
      },
    );
  }

  consultarEstadoAgt(): void {
    this.consultandoEstado.set(true);
    this.faturacaoService.consultarEstado(this.documentoId).subscribe({
      next: (docAtualizado) => {
        this.consultandoEstado.set(false);
        this.documento.set(docAtualizado);
        this.feedback.showSuccess(
          docAtualizado,
          `Estado fiscal atualizado junto da AGT: ${docAtualizado.estado}.`,
        );
        if (this.isAceite(docAtualizado)) {
          this.carregarSaldoERecibos();
        }
      },
      error: (err) => {
        this.consultandoEstado.set(false);
        this.feedback.showError(
          err,
          'Não foi possível consultar o estado na AGT neste momento. O serviço pode estar a processar em fila.',
        );
      },
    });
  }

  corrigirFatura(): void {
    this.abrirConfirmacao(
      'Retificar Fatura Rejeitada',
      `Deseja iniciar o processo de retificação da fatura rejeitada ${this.documento()?.documentNo}? Será gerado um novo documento corretivo no estado Guardada mantendo a referência fiscal da rejeição.`,
      () => {
        this.router.navigate([
          '/admin/financeiro',
          this.documentoId,
          'corrigir',
        ]);
      },
    );
  }

  editarFatura(): void {
    if (!this.isGuardada()) {
      this.feedback.showWarning(
        'Apenas faturas no estado Guardada podem ser editadas diretamente.',
      );
      return;
    }
    this.router.navigate(['/admin/financeiro', this.documentoId, 'editar']);
  }

  descarregarPdf(formato: FormatoDocumentoPdf = FormatoDocumentoPdf.A4): void {
    this.downloadingPdf.set(true);
    const doc = this.documento();
    const nomeFicheiro = doc?.documentNo
      ? doc.documentNo.replace(/\s+/g, '_').replace(/[\/\\]/g, '-')
      : `Fatura_${this.documentoId}`;

    this.faturacaoService.descarregarPdf(this.documentoId, formato).subscribe({
      next: (blob) => {
        this.downloadingPdf.set(false);
        this.faturacaoService.salvarFicheiroPdf(
          blob,
          `${nomeFicheiro}_${formato === FormatoDocumentoPdf.A4 ? 'A4' : 'Ticket'}.pdf`,
        );
        this.feedback.showSuccess(
          `Ficheiro PDF ${nomeFicheiro} descarregado com sucesso.`,
        );
      },
      error: (err) => {
        this.downloadingPdf.set(false);
        this.feedback.showError(
          err,
          'Erro ao descarregar o ficheiro PDF da fatura.',
        );
      },
    });
  }

  descarregarPdfRecibo(idRecibo: number, docNo: string): void {
    this.actionLoading.set(true);
    const nomeFicheiro = docNo
      ? docNo.replace(/\s+/g, '_').replace(/[\/\\]/g, '-')
      : `Recibo_${idRecibo}`;

    this.faturacaoService
      .descarregarPdf(idRecibo, FormatoDocumentoPdf.A4)
      .subscribe({
        next: (blob) => {
          this.actionLoading.set(false);
          this.faturacaoService.salvarFicheiroPdf(
            blob,
            `${nomeFicheiro}_A4.pdf`,
          );
          this.feedback.showSuccess(
            `Recibo fiscal ${nomeFicheiro} descarregado com sucesso.`,
          );
        },
        error: (err) => {
          this.actionLoading.set(false);
          this.feedback.showError(err, 'Erro ao descarregar o PDF do recibo.');
        },
      });
  }

  gerarReferenciaMulticaixa(): void {
    this.actionLoading.set(true);
    this.faturacaoService.gerarReferenciaPagamento(this.documentoId).subscribe({
      next: (res) => {
        this.actionLoading.set(false);
        this.feedback.showSuccess(
          res,
          'Referência de pagamento Multicaixa gerada com sucesso.',
        );
        this.carregarDadosCompletos();
      },
      error: (err) => {
        this.actionLoading.set(false);
        this.feedback.showError(err, 'Erro ao gerar referência Multicaixa.');
      },
    });
  }

  abrirModalSimular(): void {
    const s = this.saldo();
    this.simularValor = s?.saldoEmDivida ?? this.documento()?.grossTotal ?? 0;
    this.modalSimularVisible = true;
  }

  executarSimularPagamento(): void {
    if (!this.simularValor || this.simularValor <= 0) {
      this.feedback.showWarning('Informe um montante válido para o pagamento.');
      return;
    }

    this.simulandoPagamento = true;
    this.faturacaoService
      .simularPagamento(this.documentoId, { amount: this.simularValor })
      .subscribe({
        next: (res) => {
          this.simulandoPagamento = false;
          this.modalSimularVisible = false;
          this.feedback.showSuccess(
            res,
            'Pagamento liquidado com sucesso. O recibo fiscal foi gerado automaticamente.',
          );
          this.carregarDadosCompletos();
        },
        error: (err) => {
          this.simulandoPagamento = false;
          this.feedback.showError(
            err,
            'Erro ao processar a liquidação do pagamento.',
          );
        },
      });
  }

  copiarTexto(texto?: string | null, nomeCampo = 'Conteúdo'): void {
    if (!texto) return;
    navigator.clipboard.writeText(texto).then(() => {
      this.feedback.showInfo(
        `${nomeCampo} copiado para a área de transferência.`,
      );
    });
  }

  abrirConfirmacao(titulo: string, mensagem: string, acao: () => void): void {
    this.acaoPendente = { titulo, mensagem, executar: acao };
    this.modalConfirmarAcao = true;
  }

  confirmarAcao(): void {
    if (this.acaoPendente) {
      const exec = this.acaoPendente.executar;
      this.modalConfirmarAcao = false;
      this.acaoPendente = null;
      exec();
    }
  }

  cancelarAcao(): void {
    this.modalConfirmarAcao = false;
    this.acaoPendente = null;
  }

  formatCurrency(value?: number | null): string {
    if (value === null || value === undefined) return '0,00 Kz';
    return (
      new Intl.NumberFormat('pt-AO', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value) + ' Kz'
    );
  }

  getStatusClass(estado?: string): string {
    switch (estado) {
      case 'Aceite':
        return 'status-aceite';
      case 'Submetida':
        return 'status-submetida';
      case 'Guardada':
        return 'status-guardada';
      case 'Rejeitada':
        return 'status-rejeitada';
      default:
        return 'status-default';
    }
  }
}
